import React, { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import useWindowSize from '@/hooks/useWindowSize';
import { Reminder } from '@/components/SvgComponents/Reminder';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { SuccessModal } from '@/components/ui/Modals/Success/SuccessModal';
import {
  Modal,
  Input,
  ErrorModal,
  Button,
  ScreenLoader,
  Breadcrumbs,
} from '@/components/ui';
import { envVariables } from '@/constants/index';
import { GalleryArrow } from '@/components/SvgComponents/GalleryArrow';
import { hooks, actions, selectors } from '@/domains/mini-market';
import { extractItemId } from '@/constants/helper';

import { ImageModal } from './ImageModal';
import { DeviceOptions } from './DeviceOptions';
import { DeviceCharacteristics } from './DeviceCharacteristics';
import { DeviceOffers } from './DeviceOffers';
import { DeviceImages } from './DeviceImages';
import { MainImage } from './DeviceImages/MainImage';
import { ImageSlider } from './DeviceImages/ImageSlider';

import styles from './index.module.scss';

export const DeviceDetails = ({ data }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [captcha, setCaptcha] = useState('');
  const [reminderTrigger, { error, isLoading }] = hooks.useCompleteOrder({
    captcha,
  });
  const [buttonVisible, setButtonVisible] = useState(false);
  const [windowsX] = useWindowSize();
  const [modalActive, setModalActive] = useState(false);
  const [reminderModalActive, setReminderModalActive] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const { t, lang } = useTranslation();
  const FormSchema = Yup.object().shape({
    phone: Yup.string()
      .matches(/^\+994-\d{2}-\d{3}-\d{2}-\d{2}$/, t('common:Required'))
      .required(t('common:Required')),
    'g-recaptcha-response': Yup.string().required(
      'Please complete the reCAPTCHA',
    ),
  });

  const captchaRef = useRef(null);
  const formik = useFormik({
    initialValues: {
      phone: '',
      product_id: data?.id,
    },
    validationSchema: FormSchema,
    isInitialValid: false,
    onSubmit: async (values) => {
      const requestData = {
        phone: values?.phone.replace(/[-]/g, ''),
        product_id: data?.id,
        'g-recaptcha-response': values['g-recaptcha-response'],
      };
      try {
        await reminderTrigger({ lang, data: requestData }).unwrap();
        setShowSuccess(true);
      } catch (error) {
        setShowError(true);
      } finally {
        formik.resetForm();
      }
    },
  });

  const submitHandler = (e) => {
    e.preventDefault();
    formik.handleSubmit();
    setReminderModalActive(false);
  };

  const handleOrderClick = () => {
    router.push({
      pathname: '/order',
      query: {
        itemId: router.query.dynamicPage[0],
        webview: router.query.webview,
      },
    });
  };

  if (!reminderModalActive) {
    captchaRef?.current?.reset();
  }

  useEffect(() => {
    const handleScroll = () => {
      const { scrollY } = window;
      if (scrollY > 155) {
        setButtonVisible(true);
      } else {
        setButtonVisible(false);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const { selectedImage } = useSelector(selectors.selectMiniMarket);

  useEffect(() => {
    dispatch(actions.setSelectedImage(data?.album?.photos?.[0]));
  }, []);

  const modalCloser = () => {
    setReminderModalActive(false);
    formik.resetForm();
  };

  useEffect(() => {
    dispatch(actions.setSelectedImage(data?.album?.photos?.[0]));
  }, [router.asPath]);

  const scrollableDivRef = useRef(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const handleNextArrowClick = () => {
    if (buttonDisabled) return;

    setButtonDisabled(true);

    if (scrollableDivRef.current) {
      const currentIndex = data?.album?.photos?.findIndex(
        (i) => i === selectedImage,
      );

      if (currentIndex !== data?.album?.photos?.length - 1) {
        dispatch(
          actions.setSelectedImage(data?.album?.photos[currentIndex + 1]),
        );
      }

      const scrollContainer = scrollableDivRef.current;
      const selectedImageElement = scrollContainer.querySelector(
        `[dataImageId='${selectedImage}']`,
      );

      if (selectedImageElement) {
        const scrollContainerRect = scrollContainer.getBoundingClientRect();
        const selectedImageRect = selectedImageElement.getBoundingClientRect();

        if (selectedImageRect.bottom > scrollContainerRect.bottom) {
          scrollContainer.scrollBy({
            top: 160,
            behavior: 'smooth',
          });
        }
      }
    }

    // Re-enable the button after 500ms
    setTimeout(() => {
      setButtonDisabled(false);
    }, 500);
  };

  const handlePrevArrowClick = () => {
    if (buttonDisabled) return;

    setButtonDisabled(true);
    if (scrollableDivRef.current) {
      setButtonDisabled(true);
      const currentIndex = data?.album?.photos?.findIndex(
        (i) => i === selectedImage,
      );
      if (currentIndex !== 0) {
        dispatch(
          actions.setSelectedImage(data?.album?.photos[currentIndex - 1]),
        );
      }
      const scrollContainer = scrollableDivRef.current;
      const selectedImageElement = scrollContainer.querySelector(
        `[dataImageId='${selectedImage}']`,
      );
      if (selectedImageElement) {
        const scrollContainerRect = scrollContainer.getBoundingClientRect();
        const selectedImageRect = selectedImageElement.getBoundingClientRect();
        if (selectedImageRect.top < scrollContainerRect.top) {
          scrollContainer.scrollBy({
            top: -160,
            behavior: 'smooth',
          });
        }
      }
    }
    setTimeout(() => {
      setButtonDisabled(false);
    }, 500);
  };
  const breadcrumbData = [
    { text: t('common:Home'), slug: 'home' },
    { text: t('common:Shop'), slug: 'bakcell-shop' },
    { text: data?.name, slug: data?.name },
  ];
  return (
    <>
      <Breadcrumbs data={breadcrumbData} className={styles.customBreadcrumbs} />
      <div className='wrapper section'>
        <div className={styles.firstTitle}>{data?.name}</div>
        {windowsX < 768 && (
          <>
            <div
              className={styles.mobileBadges}
              itemScope
              itemType='https://schema.org/Product'
            >
              {data?.labels?.map((label) => {
                let cleanedStr = label?.title?.replace(/<\/?b>/g, '');
                cleanedStr = cleanedStr
                  ?.replace(/\\n/g, ' ')
                  .replace(/<\/?br>/g, '');
                return (
                  <div
                    key={label?.id}
                    className={classNames([
                      styles.singleBadge,
                      {
                        [styles.singleBadge__deep_red]:
                          label?.color === 'deep_red',
                        [styles.singleBadge__lighter_red]:
                          label?.color === 'lighter_red',
                      },
                    ])}
                  >
                    <Image
                      alt='small icon'
                      title='Small Icon'
                      src={label?.icon}
                      width={24}
                      height={24}
                    />
                    <p>{cleanedStr}</p>
                  </div>
                );
              })}
            </div>
            <ImageSlider
              images={data?.album?.photos}
              setModalActive={setModalActive}
            />
          </>
        )}

        <div
          className={styles.customWrapper}
          itemScope
          itemType='https://schema.org/Product'
        >
          {windowsX >= 768 && (
            <div className={styles.left}>
              <div className={styles.imageWrapper}>
                <div className={styles.images}>
                  {data?.album?.photos.length > 5 && (
                    <GalleryArrow
                      onClick={handlePrevArrowClick}
                      className={styles.prev}
                    />
                  )}

                  <div className={styles.overflower} ref={scrollableDivRef}>
                    <DeviceImages images={data?.album?.photos} />
                  </div>

                  {data?.album?.photos.length > 5 && (
                    <GalleryArrow
                      onClick={handleNextArrowClick}
                      className={styles.next}
                    />
                  )}
                </div>

                <div className={styles.mainImage}>
                  <MainImage
                    isModal={false}
                    photos={data?.album?.photos}
                    setModalActive={setModalActive}
                    data={data}
                  />
                  <div className={styles.badges}>
                    {data?.labels?.map((label) => {
                      const regex = /\\n/g;
                      return (
                        <div
                          key={label?.id}
                          className={`${styles.singleBadge} ${
                            label?.color === 'deep_red' &&
                            styles.singleBadge__deep_red
                          } ${
                            label?.color === 'lighter_red' &&
                            styles.singleBadge__lighter_red
                          }`}
                        >
                          {label?.icon && (
                            <Image
                              alt='small icon'
                              title='Small Icon'
                              src={label?.icon}
                              width={24}
                              height={24}
                            />
                          )}
                          <p
                            dangerouslySetInnerHTML={{
                              __html: label?.title?.replace(regex, '<br>'),
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              {buttonVisible && (
                <div
                  className={` ${styles.buttonVisible} ${
                    buttonVisible && styles.buttonVisible__visible
                  }`}
                >
                  <div className={styles.priceContainer}>
                    {data?.new_price ? (
                      <>
                        {data?.old_price && (
                          <p className={styles.discountPrice}>
                            {data?.old_price}
                            {data?.currency}
                          </p>
                        )}
                        <p>
                          {data?.new_price} {data?.currency}
                        </p>
                      </>
                    ) : (
                      data?.old_price && (
                        <p>
                          {data?.old_price} {data?.currency}
                        </p>
                      )
                    )}
                  </div>

                  {data?.in_stock ? (
                    <Button id='testingId' onClick={handleOrderClick}>
                      {t('common:OrderNow')}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setReminderModalActive(true)}
                      variant='outline'
                    >
                      <Reminder /> {t('common:RemindMe')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={styles.right}>
            <div className={styles.top}>
              <div className={styles.leftWrapper}>
                <div className={styles.left}>
                  <div className={styles.buttons}>
                    {data?.in_stock ? (
                      <span className={styles.inStock}>
                        {t('common:inStock')}
                      </span>
                    ) : (
                      <span className={styles.outStock}>
                        {t('common:outOfStock')}
                      </span>
                    )}
                    {data?.highlights?.map((highlightItem) => {
                      return (
                        <span key={highlightItem?.id} className={styles.edv}>
                          {highlightItem?.icon && (
                            <Image
                              width={20}
                              height={20}
                              alt={highlightItem?.title}
                              title={highlightItem?.title}
                              src={highlightItem?.icon}
                            />
                          )}{' '}
                          {highlightItem?.title}
                        </span>
                      );
                    })}
                  </div>
                  <div className={styles.priceContainer}>
                    {data?.new_price ? (
                      <>
                        {data?.old_price && (
                          <p className={styles.discountPrice}>
                            {data?.old_price}
                            {data?.currency}
                          </p>
                        )}
                        <p>
                          {data?.new_price} {data?.currency}
                        </p>
                      </>
                    ) : (
                      data?.old_price && (
                        <p>
                          {data?.old_price} {data?.currency}
                        </p>
                      )
                    )}
                  </div>
                  <p className={styles.description}>{data?.description}</p>
                  <DeviceOptions
                    capacities={data?.storages}
                    selectedCapacity={data?.storage}
                    colors={data?.colors}
                    selectedColor={data?.color}
                    variations={data?.all_variations}
                  />
                  {!buttonVisible && (
                    <div className={styles.buttonContainer}>
                      {data?.in_stock ? (
                        <Button onClick={handleOrderClick}>
                          {t('common:OrderNow')}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setReminderModalActive(true)}
                          variant='outline'
                        >
                          <Reminder /> {t('common:RemindMe')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <DeviceOffers data={data} />
            </div>
            <DeviceCharacteristics data={data} />
          </div>
        </div>
      </div>
      {windowsX < 768 && !reminderModalActive && (
        <div className={styles.mobileFixed}>
          <div className={`${styles.custom} wrapper`}>
            <div className={styles.priceMobile}>
              {data?.new_price ? (
                <>
                  {data?.old_price && (
                    <p className={styles.discountPrice}>
                      {data?.old_price}
                      {data?.currency}
                    </p>
                  )}
                  <p className={styles.price}>
                    {data?.new_price} {data?.currency}
                  </p>
                </>
              ) : (
                data?.old_price && (
                  <p className={styles.price}>
                    {data?.old_price} {data?.currency}
                  </p>
                )
              )}
            </div>
            <div className={styles.btn}>
              {data?.in_stock ? (
                <Button onClick={handleOrderClick}>
                  {t('common:OrderNow')}
                </Button>
              ) : (
                <Button
                  onClick={() => setReminderModalActive(true)}
                  variant='outline'
                >
                  <Reminder /> {t('common:RemindMe')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <ImageModal
        device={data}
        modalActive={modalActive}
        isMobileView={windowsX < 768}
        setModalActive={setModalActive}
        setReminderModalActive={() => setReminderModalActive(true)}
        itemScope
        itemType='https://schema.org/ImageGallery'
      />
      <div className={styles.remindModal}>
        <Modal active={reminderModalActive} setActive={modalCloser}>
          <h1>{t('common:RemindMe')}</h1>
          <p>{t('common:ReminderText')}</p>
          <form onSubmit={(e) => submitHandler(e)}>
            <Input
              className={styles.input}
              onBlur={formik.handleBlur('phone')}
              error={formik.touched.phone && formik.errors.phone}
              value={formik.values.phone}
              type='tel'
              onChange={(value) => {
                const formattedPhone = formatPhoneNumber(value);
                formik.setFieldValue('phone', formattedPhone);
              }}
              label={t('common:Phone')}
              placeholder={'+994-XX-XXX-XX-XX'}
              mask={'+\\9\\9\\4-99-999-99-99'}
            />
            <ReCAPTCHA
              sitekey={envVariables.NEXT_PUBLIC_RECAPTCHA_API_KEY || 'default'}
              onChange={(recaptcha) =>
                formik.setFieldValue('g-recaptcha-response', recaptcha)
              }
              error={
                formik.touched['g-recaptcha-response'] &&
                formik.errors['g-recaptcha-response']
              }
              ref={captchaRef}
              hl={lang}
            />
            <Button disabled={!formik.isValid}>{t('common:Continue')}</Button>
          </form>
        </Modal>
      </div>
      <SuccessModal
        active={showSuccess}
        setSuccessModal={() => setShowSuccess(false)}
        title={t('common:ReminderSuccessTitle')}
        description={t('common:ReminderSuccessText')}
        itemScope
        itemType='https://schema.org/Thing'
      />

      <ErrorModal
        icon='/assets/error.png'
        active={showError}
        setActive={() => setShowError(false)}
        itemScope
        itemType='https://schema.org/Thing'
      >
        {error?.message}
      </ErrorModal>
      <ScreenLoader
        visible={isLoading}
        itemScope
        itemType='https://schema.org/Thing'
      />
    </>
  );
};
